import React, { useState, useEffect } from 'react';
import { Building2, ArrowRight, Loader2 } from 'lucide-react';
import api from '../services/api';

/**
 * ManufacturersTooltip - Rich tooltip showing manufacturers grouped by main group
 * with merged row headers, parts count, and visual density bars
 */
const ManufacturersTooltip = ({ manufacturers, onManufacturerClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Get manufacturers by main group
      const response = await api.get('/api/v1/parts/manufacturers/by-group');
      const byGroup = response.data?.by_group || {};
      const allManufacturers = response.data?.all_manufacturers || [];
      
      // Find max parts for density calculation
      let maxParts = 1;
      Object.values(byGroup).forEach(group => {
        group.manufacturers?.forEach(mfr => {
          if ((mfr.part_count || 0) > maxParts) {
            maxParts = mfr.part_count;
          }
        });
      });

      // Sort groups by total parts
      const sortedGroups = Object.entries(byGroup)
        .map(([code, data]) => ({
          code,
          name: data.name || code,
          manufacturers: (data.manufacturers || []).sort((a, b) => (b.part_count || 0) - (a.part_count || 0))
        }))
        .sort((a, b) => {
          const aTotal = a.manufacturers.reduce((sum, m) => sum + (m.part_count || 0), 0);
          const bTotal = b.manufacturers.reduce((sum, m) => sum + (m.part_count || 0), 0);
          return bTotal - aTotal;
        })
        .slice(0, 5); // Show top 5 groups

      // Find most versatile manufacturer (appears in most groups)
      const mfrGroupCount = {};
      Object.values(byGroup).forEach(group => {
        group.manufacturers?.forEach(mfr => {
          mfrGroupCount[mfr.name] = (mfrGroupCount[mfr.name] || 0) + 1;
        });
      });
      
      const mostVersatile = Object.entries(mfrGroupCount)
        .sort((a, b) => b[1] - a[1])[0];

      setData({
        groups: sortedGroups,
        maxParts,
        total: allManufacturers.length,
        insight: mostVersatile ? `${mostVersatile[0]} spans ${mostVersatile[1]} categories (most versatile)` : null
      });
    } catch (error) {
      console.error('Failed to load manufacturers data:', error);
      // Fallback to basic manufacturer list
      if (manufacturers?.length > 0) {
        const maxParts = Math.max(...manufacturers.map(m => m.count || 0), 1);
        setData({
          groups: [{
            code: 'ALL',
            name: 'All Categories',
            manufacturers: manufacturers.slice(0, 10).map(m => ({
              name: m.name,
              part_count: m.count
            }))
          }],
          maxParts,
          total: manufacturers.length,
          insight: null
        });
      } else {
        setData({ groups: [], maxParts: 1, total: 0, insight: null });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 px-4 py-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-white" />
            <span className="font-semibold text-white text-sm">Manufacturers</span>
          </div>
        </div>
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-white" />
            <span className="font-semibold text-white text-sm">Manufacturers</span>
          </div>
          <span className="text-cyan-200 text-xs">{data.total} total</span>
        </div>
      </div>

      {/* Table */}
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 font-medium text-slate-500">Category / Manufacturer</th>
              <th className="text-right px-3 py-2 font-medium text-slate-500">Parts</th>
              <th className="px-3 py-2 font-medium text-slate-500 w-16">Share</th>
            </tr>
          </thead>
          <tbody>
            {data.groups.map((mainGroup, groupIndex) => (
              <React.Fragment key={mainGroup.code}>
                {/* Main Group Header Row */}
                <tr className="bg-gradient-to-r from-slate-50 to-slate-100">
                  <td colSpan={3} className="px-3 py-2 font-semibold text-slate-700 border-b border-slate-200">
                    <span className="font-mono text-purple-600">{mainGroup.code}</span>
                    <span className="ml-1.5">{mainGroup.name}</span>
                  </td>
                </tr>
                
                {/* Top 3 Manufacturers */}
                {mainGroup.manufacturers.slice(0, 3).map((mfr, mfrIndex) => {
                  const density = data.maxParts > 0 ? ((mfr.part_count || 0) / data.maxParts) * 100 : 0;
                  const isTop = mfrIndex === 0;
                  return (
                    <tr 
                      key={`${mainGroup.code}-${mfr.name}`}
                      onClick={() => onManufacturerClick?.(mfr.name)}
                      className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-2 pl-6">
                        <span className={`${isTop ? 'text-slate-700 font-medium' : 'text-slate-600'}`}>
                          {mfr.name}
                        </span>
                        {isTop && (
                          <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">
                            Top
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-700">
                        {(mfr.part_count || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-cyan-500 rounded-full transition-all"
                            style={{ width: `${Math.max(density, 2)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                
                {/* +N more indicator */}
                {mainGroup.manufacturers.length > 3 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-1.5 pl-6 text-slate-400 text-xs">
                      +{mainGroup.manufacturers.length - 3} more manufacturers
                    </td>
                    <td className="px-3 py-1.5 text-right text-slate-400">
                      {mainGroup.manufacturers.slice(3).reduce((sum, m) => sum + (m.part_count || 0), 0)}
                    </td>
                    <td></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Insight Footer */}
      {data.insight && (
        <div className="px-3 py-2 bg-amber-50 border-t border-amber-100">
          <p className="text-xs text-amber-800">
            <span className="font-semibold">💡</span> {data.insight}
          </p>
        </div>
      )}

      {/* View All Link */}
      <div className="px-3 py-2 bg-slate-50 border-t border-slate-200">
        <button 
          onClick={() => {/* Could open a manufacturers modal */}}
          className="w-full text-xs text-cyan-600 hover:text-cyan-800 font-medium flex items-center gap-1 justify-center"
        >
          View All Manufacturers
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default ManufacturersTooltip;
